'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface VideoTutorial {
  id: number;
  title: string;
  description: string;
  duration: string;
  thumbnail: string;
  alt: string;
  category: string;
  views: string;
}

interface VideoTutorialsProps {
  className?: string;
}

export default function VideoTutorials({ className = '' }: VideoTutorialsProps) {
  const [selectedVideo, setSelectedVideo] =
    useState<VideoTutorial | null>(null);
  const [watchedVideos, setWatchedVideos] = useState<number[]>([]);

  const tutorials: VideoTutorial[] = [
    {
      id: 1,
      title: 'Getting Started with ParkPing',
      description:
        'Learn how to activate and set up your ParkPing card in under 2 minutes',
      duration: '2:15',
      thumbnail:
        'https://img.rocket.new/generatedImages/rocket_gen_img_1c7708f49-1768078098948.png',
      alt: 'Person holding smartphone scanning QR code on car dashboard with ParkPing card visible',
      category: 'Setup',
      views: '12.5K',
    },
    {
      id: 2,
      title: 'Installing Your Card',
      description:
        'Step-by-step guide to properly install your ParkPing card on your vehicle',
      duration: '1:45',
      thumbnail:
        'https://images.unsplash.com/photo-1707296916319-037ad32dc7f6',
      alt: 'Close-up of hands placing white ParkPing card on car windshield interior',
      category: 'Installation',
      views: '8.3K',
    },
    {
      id: 3,
      title: 'Privacy Settings Explained',
      description:
        'Understand how to manage your privacy settings and control who can reach you',
      duration: '3:20',
      thumbnail:
        'https://img.rocket.new/generatedImages/rocket_gen_img_1eef6d7c6-1768819134333.png',
      alt: 'Smartphone screen showing ParkPing app privacy settings interface with toggle switches',
      category: 'Privacy',
      views: '15.2K',
    },
    {
      id: 4,
      title: 'Managing Multiple Cards',
      description:
        'How to add and manage multiple ParkPing cards for your family vehicles',
      duration: '2:50',
      thumbnail:
        'https://img.rocket.new/generatedImages/rocket_gen_img_126ba313f-1768819130054.png',
      alt: 'Dashboard view showing three ParkPing cards arranged on table with smartphone',
      category: 'Account',
      views: '6.7K',
    },
    {
      id: 5,
      title: 'Troubleshooting Common Issues',
      description:
        'Quick fixes for the most common ParkPing card issues',
      duration: '4:10',
      thumbnail:
        'https://img.rocket.new/generatedImages/rocket_gen_img_1cc38bc20-1767706224574.png',
      alt: 'Support representative at desk with headset helping customer via video call',
      category: 'Support',
      views: '9.1K',
    },
    {
      id: 6,
      title: 'Bulk Order Setup for Societies',
      description:
        'Guide for housing society administrators to set up ParkPing for residents',
      duration: '5:30',
      thumbnail:
        'https://images.unsplash.com/photo-1609919103947-a206217d48c0',
      alt: 'Modern apartment complex parking area with multiple cars and organized parking spaces',
      category: 'Enterprise',
      views: '4.2K',
    },
  ];

  const handleVideoClick = (video: VideoTutorial) => {
    setSelectedVideo(video);
    if (!watchedVideos.includes(video.id)) {
      setWatchedVideos([...watchedVideos, video.id]);
    }
  };

  const closeModal = () => {
    setSelectedVideo(null);
  };

  return (
    <section
      id="video-tutorials"
      className={`py-16 bg-white ${className}`}
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Video Tutorials
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Watch our comprehensive video guides to get the most out of your
            ParkPing experience
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tutorials.map((tutorial) => (
            <div
              key={tutorial.id}
              onClick={() => handleVideoClick(tutorial)}
              className="group bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-[#42AF47]/40 hover:shadow-md transition cursor-pointer"
            >
              {/* Thumbnail */}
              <div className="relative aspect-video overflow-hidden">
                <AppImage
                  src={tutorial.thumbnail}
                  alt={tutorial.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition"
                />

                {/* Hover Play */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <div className="w-16 h-16 bg-[#42AF47] rounded-full flex items-center justify-center">
                    <Icon
                      name="PlayIcon"
                      size={32}
                      className="text-white ml-1"
                    />
                  </div>
                </div>

                {/* Duration */}
                <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                  {tutorial.duration}
                </div>

                {/* Watched */}
                {watchedVideos.includes(tutorial.id) && (
                  <div className="absolute top-2 right-2 bg-[#42AF47] text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Icon name="CheckCircleIcon" size={14} />
                    <span>Watched</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-[#42AF47] font-medium">
                    {tutorial.category}
                  </span>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs text-gray-500">
                    {tutorial.views} views
                  </span>
                </div>

                <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-[#42AF47] transition">
                  {tutorial.title}
                </h3>

                <p className="text-sm text-gray-600 line-clamp-2">
                  {tutorial.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {selectedVideo && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <div
              className="bg-white rounded-lg max-w-4xl w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">
                  {selectedVideo.title}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-900 transition"
                  aria-label="Close video"
                >
                  <Icon name="XMarkIcon" size={24} />
                </button>
              </div>

              <div className="aspect-video bg-black flex items-center justify-center">
                <div className="text-center text-white">
                  <Icon
                    name="PlayCircleIcon"
                    size={64}
                    className="mx-auto mb-4 opacity-50"
                  />
                  <p className="text-sm opacity-75">
                    Video player would load here
                  </p>
                  <p className="text-xs opacity-50 mt-2">
                    Duration: {selectedVideo.duration}
                  </p>
                </div>
              </div>

              <div className="p-4">
                <p className="text-gray-600">
                  {selectedVideo.description}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
